/**
 * React Query hook for fetching audit error summary
 *
 * Returns counts of errors grouped by priority tier (must_fix, should_fix, informational)
 */

import { useQuery } from "@tanstack/react-query";
import { authorizedAxios } from "../axios";
import { useAuth0 } from "@auth0/auth0-react";

export interface AuditSummary {
  must_fix: number;
  should_fix: number;
  informational: number;
  total: number;
}

export interface ErrorTypeCount {
  errorType: string;
  priority: string;
  count: number;
}

export interface AuditSummaryResponse {
  success: boolean;
  data: {
    summary: AuditSummary;
    errorTypes: ErrorTypeCount[];
  };
}

export const useAuditSummary = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<AuditSummaryResponse>({
    queryKey: ["audit", "summary"],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).get("/audit/summary");
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 2,
  });
};
